import type { Release } from '@src/dto/Release';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';

interface DeleteReleaseDialogProps {
  releasePendingDelete: Release | null;
  deleteReason: string;
  isDeletingRelease: boolean;
  onClose: () => void;
  onDelete: () => void | Promise<void>;
  onDeleteReasonChange: (reason: string) => void;
}

export function DeleteReleaseDialog({
  releasePendingDelete,
  deleteReason,
  isDeletingRelease,
  onClose,
  onDelete,
  onDeleteReasonChange,
}: DeleteReleaseDialogProps) {
  return (
    <Dialog
      open={!!releasePendingDelete}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Release</DialogTitle>
          <DialogDescription>
            Only superadmins can delete releases. This performs a soft delete,
            removes the release from portal views, and leaves an audit trail.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3">
            <div className="text-sm font-medium text-red-100">
              {releasePendingDelete?.versionName || 'Unknown release'}
              {releasePendingDelete?.versionCode
                ? ` (${releasePendingDelete.versionCode})`
                : ''}
            </div>
            <div className="mt-1 text-xs text-red-200/80">
              {releasePendingDelete?.releaseFileName}
            </div>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="release-delete-reason"
              className="text-sm font-medium text-white"
            >
              Reason for deletion (optional)
            </label>
            <Textarea
              id="release-delete-reason"
              value={deleteReason}
              onChange={(event) => onDeleteReasonChange(event.target.value)}
              placeholder="Add context for the audit trail"
              className="border-neutral-700 bg-neutral-950 text-white placeholder:text-neutral-500"
              rows={4}
              maxLength={500}
              disabled={isDeletingRelease}
            />
          </div>
        </DialogBody>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isDeletingRelease}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onDelete}
            disabled={isDeletingRelease}
          >
            {isDeletingRelease ? 'Deleting...' : 'Delete Release'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
